import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Mountain, CheckCircle2, Activity, Flag, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import BulkIO from "@/components/BulkIO";

const EPIC_STATUS = {
  backlog:     { label: "Backlog",     cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  planned:     { label: "Planned",     cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  on_hold:     { label: "On hold",     cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  completed:   { label: "Completed",   cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  cancelled:   { label: "Cancelled",   cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};
const PRIORITIES = ["low", "medium", "high", "critical"];
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function EpicForm({ open, onOpenChange, projectId, editing, employees, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", owner_id: "", status: "backlog", priority: "medium", start_date: "", target_date: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(editing
        ? { ...editing, owner_id: editing.owner_id || "", start_date: editing.start_date || "", target_date: editing.target_date || "" }
        : { name: "", description: "", owner_id: "", status: "backlog", priority: "medium", start_date: "", target_date: "" });
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.owner_id) payload.owner_id = null;
      if (!payload.start_date) payload.start_date = null;
      if (!payload.target_date) payload.target_date = null;
      if (editing) await api.patch(`/epics/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/epics`, payload);
      toast.success(editing ? "Epic updated" : "Epic created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="epic-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Epic" : "New Epic"}</DialogTitle>
          <DialogDescription>Large body of work that groups related features and tasks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <UILabel>Name *</UILabel>
            <Input data-testid="epic-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <UILabel>Description</UILabel>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <UILabel>Status</UILabel>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="epic-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(EPIC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <UILabel>Priority</UILabel>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <UILabel>Owner</UILabel>
            <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="epic-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">Unassigned</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><UILabel>Start date</UILabel><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><UILabel>Target date</UILabel><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="epic-submit" onClick={submit} disabled={saving}>{saving ? "Saving…" : (editing ? "Save" : "Create epic")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Epics() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [params, setParams] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [epics, setEpics] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (!projectId && r.data.length) setProjectId(r.data[0].id); });
    api.get("/employees").then((r) => setEmployees(r.data));
    // eslint-disable-next-line
  }, []);

  const reload = async () => {
    if (!projectId) return;
    const { data } = await api.get(`/projects/${projectId}/epics`);
    setEpics(data);
    setParams({ project: projectId }, { replace: true });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const canWrite = useMemo(() => {
    const p = projects.find((x) => x.id === projectId);
    return user?.role === "admin" || (p && (p.manager_id === user?.id || (user?.role === "branch_admin" && p?.created_by === user?.id)));
  }, [projects, projectId, user]);

  const filtered = useMemo(() =>
    statusFilter === "all" ? epics : epics.filter((e) => e.status === statusFilter),
    [epics, statusFilter]);

  const summary = useMemo(() => ({
    total: epics.length,
    active: epics.filter((e) => e.status === "in_progress").length,
    done: epics.filter((e) => e.status === "completed").length,
    avg: epics.length ? Math.round(epics.reduce((s, e) => s + (e.progress || 0), 0) / epics.length) : 0,
  }), [epics]);

  const removeEpic = async (e) => {
    if (!window.confirm(`Delete ${e.identifier}? Child features & tasks stay — their link to this epic is cleared.`)) return;
    try { await api.delete(`/epics/${e.id}`); toast.success("Epic deleted"); reload(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="epics-header" title="Epics" subtitle="Large bodies of work that span multiple features & tasks"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <BulkIO
              entityLabel="Epic" entityPlural="Epics"
              exportUrl={`/api/epics/export.csv${projectId ? `?project_id=${projectId}` : ""}`}
              templateUrl="/api/epics/import/template.csv"
              importUrl="/epics/import"
              columns="name · description · project_code · status · priority · owner_email · start_date · target_date"
              canImport={user?.role === "admin"}
              onImported={reload}
            />
            {canWrite && projectId && (
              <Button data-testid="new-epic-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" />New Epic
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="epic-kpis">
        {[
          { label: "Total Epics",    value: summary.total,  icon: Mountain,     color: "text-primary" },
          { label: "In Progress",    value: summary.active, icon: Activity,     color: "text-amber-500" },
          { label: "Completed",      value: summary.done,   icon: CheckCircle2, color: "text-emerald-500" },
          { label: "Avg Progress %", value: summary.avg,    icon: Flag,         color: "text-violet-500" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="glass rounded-xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-background/50 flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="number font-medium text-xl leading-none mt-1">{value}</div></div>
          </div>
        ))}
      </div>

      <SectionCard
        title={`${filtered.length} epic${filtered.length === 1 ? "" : "s"}`}
        subtitle={projectId ? projects.find((p) => p.id === projectId)?.name : "Select a project"}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9 w-56" data-testid="epic-project-sel"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} <span className="text-muted-foreground">· {p.code}</span></SelectItem>)}</SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="epic-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(EPIC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      >
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No epics match your filter. Create one to group features and tasks.</div>
        ) : (
          <div className="space-y-2" data-testid="epic-list">
            {filtered.map((e) => {
              const sm = EPIC_STATUS[e.status] || {};
              const owner = employees.find((emp) => emp.id === e.owner_id);
              return (
                <div key={e.id} className="glass rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`epic-row-${e.id}`}>
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0">{e.identifier}</Badge>
                  <div className="flex-1 min-w-0">
                    <Link to={`/epics/${e.id}`} className="font-medium text-sm hover:text-primary truncate block">{e.name}</Link>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden max-w-[280px]"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${e.progress || 0}%` }} /></div>
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{e.progress || 0}% · {e.completed_task_count || 0}/{e.task_count || 0}</span>
                    </div>
                  </div>
                  {owner && <div className="flex items-center gap-1 text-[11px] shrink-0" title={owner.full_name}><Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>{owner.full_name}</div>}
                  <Badge className={`${sm.cls} border text-[10px] shrink-0`}>{sm.label}</Badge>
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">{e.priority}</Badge>
                  {e.target_date && <Badge variant="outline" className="text-[10px] gap-1 shrink-0"><Flag className="w-2.5 h-2.5" />{e.target_date}</Badge>}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" data-testid={`epic-open-${e.id}`} onClick={() => nav(`/epics/${e.id}`)}>Open</Button>
                    {canWrite && (
                      <>
                        <Button size="icon" variant="ghost" data-testid={`epic-edit-${e.id}`} onClick={() => { setEditing(e); setFormOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`epic-del-${e.id}`} onClick={() => removeEpic(e)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <EpicForm open={formOpen} onOpenChange={setFormOpen} projectId={projectId} editing={editing} employees={employees} onSaved={reload} />
    </div>
  );
}
